import React, { useEffect } from "react";
import "./App.css";
import Nav from "./Nav.js";
import routes from "./routes.js";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Amplify } from "aws-amplify";
import AWSConfig from "./aws-exports";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";

import useAuth from "./hooks/useAuth";
import useTheme from "./hooks/useTheme";
import { AuthContext } from "./context/authContext.js";
import "@fontsource/open-sans";
import { Toaster } from "react-hot-toast";

Amplify.configure(AWSConfig);

if (typeof window !== "undefined") {
  const hasClearedLocalStorage = sessionStorage.getItem("hasClearedLocalStorage");
  if (!hasClearedLocalStorage) {
    localStorage.clear();
    sessionStorage.setItem("hasClearedLocalStorage", "true");
    console.log("Rebooting...")
  }
}


const App = () => {
  const { darkMode, setDarkMode, theme } = useTheme();
  const { user, loading, hasAccess, isAdmin, handleLogin } = useAuth();


  const LoggedOutPage = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#035CB8",
      }}
    >
      <Card
        sx={{
          maxWidth: "450px",
          borderRadius: "15px",
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: 3,
          p: 3,
          textAlign: "center",
        }}
      >
        <CardContent>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
          >
            You are currently logged out of the AWS Billing App.
          </Typography>
          <Button
            onClick={handleLogin}
            sx={{
              backgroundColor: "#035CB8",
              color: "white",
              "&:hover": {
                backgroundColor: "#024a93",
              },
            }}
          >
            Log In
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  const AccessDeniedPage = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Card
        sx={{
          maxWidth: "450px",
          borderRadius: "15px",
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: 3,
          p: 3,
          textAlign: "center",
        }}
      >
        <CardContent>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
          >
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ color: "#555", mb: 3 }}>
            You do not have access to the AWS Billing Site. Please contact your
            administrator for access.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading && !user) {
    return (
      <div className="container vh-100 d-flex justify-content-center align-items-center">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        ></div>
      </div>
    );
  }

  if (!loading && !hasAccess) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AccessDeniedPage />
      </ThemeProvider>
    );
  }

  return (
    <AuthContext.Provider value={{ isAdmin }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster position="top-center" reverseOrder={false} />
        <BrowserRouter>
          {!user ? null : (
            <Nav
              user={user}
              isAdmin={isAdmin}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
            />
          )}

          <Routes>
            <Route path="/logout" element={<LoggedOutPage />} />
            {user &&
              routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={route.component}
                />
              ))}
          </Routes>
          {!user && window.location.pathname !== "/logout" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                width: "100vw",
                backgroundColor: "#035CB8",
              }}
            >
              <Card
                sx={{
                  maxWidth: "450px",
                  borderRadius: "15px",
                  background: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 3,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
                  >
                    Welcome to the UMass Chan AWS Billing Site.
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#555", mb: 3 }}>
                    You will be redirected and logged in momentarily...
                  </Typography>
                  <CircularProgress sx={{ color: "#035CB8" }} size={50} />
                </CardContent>
              </Card>
            </Box>
          )}
        </BrowserRouter>
      </ThemeProvider>
    </AuthContext.Provider>
  );
};

export default App;
